import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Wand2, Loader2 } from 'lucide-react'
import { useSettingsStore } from '../../../../stores/settingsStore'
import type { GameFile } from '@creanizer/types'
import { getCurrentGeneratorModel } from '../../../../lib/generatorModelSelection'

interface GameFileGeneratorProps {
  onGenerated: (gameFile: GameFile) => void
}

export function GameFileGenerator({ onGenerated }: GameFileGeneratorProps) {
  const [prompt, setPrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { activeApiKey } = useSettingsStore()
  const currentApiKey = activeApiKey

  const examplePrompts = [
    'ダンジョン探索RPG。HPとスタミナがあり、戦闘、探索、休憩ができる。宝箱を見つけたら勝利、HPが0になったら敗北。',
    '謎解きアドベンチャー。3つの部屋があり、それぞれに謎がある。全ての謎を解いたらクリア。ヒントシステムあり。',
    'サバイバルゲーム。空腹度、体力、精神力のパラメータ。食料探し、休息、クラフトができる。30日生き延びたら勝利。',
    'ホラー脱出ゲーム。正気度パラメータあり。探索、隠れる、逃げるアクション。屋敷から脱出できたら勝利。'
  ]

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('ゲームの説明を入力してください')
      return
    }
    
    if (!currentApiKey) {
      setError('APIキーが設定されていません。設定画面からAPIキーを登録してください。')
      return
    }

    setIsGenerating(true)
    setError(null)

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787'
      const response = await fetch(`${apiUrl}/api/v1/textgame/generate-structure`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': currentApiKey?.key || ''
        },
        body: JSON.stringify({
          prompt,
          mode: 'structured',
          modelId: getCurrentGeneratorModel('textgame')
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => null)
        throw new Error(errorData?.message || `HTTPエラー: ${response.status}`)
      }

      const result = await response.json()
      
      if (result.success && result.data) {
        console.log('Generated game structure:', result.data)
        onGenerated(result.data as GameFile)
      } else {
        throw new Error(result.message || result.error || '生成に失敗しました')
      }
    } catch (error) {
      console.error('Generation error:', error)
      const errorMessage = error instanceof Error ? error.message : '不明なエラーが発生しました'
      setError(errorMessage)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>AIでゲーム構造を生成</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="game-prompt">
            ゲームの説明
            <span className="text-xs text-gray-500 ml-2">
              どんなゲームにしたいか自由に記述してください
            </span>
          </Label>
          <Textarea
            id="game-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="例: ダンジョン探索RPG。HPとスタミナがあり、戦闘と探索ができる。宝箱を見つけたら勝利。"
            rows={6}
            disabled={isGenerating}
          />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            <strong>エラー:</strong> {error}
          </div>
        )}

        <div className="space-y-2">
          <Label>参考例</Label>
          <div className="grid grid-cols-1 gap-2">
            {examplePrompts.map((example, index) => (
              <button
                key={index}
                onClick={() => setPrompt(example)}
                disabled={isGenerating}
                className="text-left p-2 text-sm border rounded hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {example}
              </button>
            ))}
          </div>
        </div>

        <Button
          onClick={handleGenerate}
          disabled={!prompt.trim() || isGenerating || !currentApiKey}
          className="w-full"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              生成中...
            </>
          ) : (
            <>
              <Wand2 className="w-4 h-4 mr-2" />
              ゲーム構造を生成
            </>
          )}
        </Button>

        {!currentApiKey && (
          <p className="text-sm text-red-500">
            APIキーが設定されていません。設定画面から設定してください。
          </p>
        )}
      </CardContent>
    </Card>
  )
}
